import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { makeApiCall } from '../../../../../helpers/utils';
import Loader from '../../../../atoms/icons/loader';
import { fetchStockId } from '../../utils/marketsUtils';
import EmptyState from '../empty-state';
import './basic-details-stock.m.css';
import { useSelector } from 'react-redux';
import get from 'lodash/get';

/* ---------------- Normalizers ---------------- */

const normalizeBasicDetails = (apiData) => {
  const listings = apiData?.data?.listings;
  if (!listings) return {};

  const mapExchange = (ex) => ({
    currentPrice: ex?.lastPrice,
    priceChange: ex?.change,
    changePercentage: ex?.percentChange,
    previousClose: ex?.prevClose,
    openPrice: ex?.open,
    dayHigh: ex?.high,
    dayLow: ex?.low,
  });

  return {
    nse: listings?.nse ? mapExchange(listings.nse) : null,
    bse: listings?.bse ? mapExchange(listings.bse) : null,
  };
};

const formatNumber = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) return '--';
  return Number(value).toFixed(decimals);
};

/* ---------------- Fetcher ---------------- */

const fetchBasicDetailsAndSector = async (marketApiEndpoint) => {
  const stockId = fetchStockId();

  const [basicRes, sectorRes] = await Promise.all([
    makeApiCall(`${marketApiEndpoint}/stocks/entities/${stockId}`),
    makeApiCall(`/next/feapi/stock/stock-dna/${stockId}`),
  ]);

  return {
    basicStockData: normalizeBasicDetails(basicRes),
    stockSector: sectorRes?.data || {},
  };
};

/* ---------------- Component ---------------- */

const BasicDetailsStock = ({ stockParentData }) => {
  const [copyAlert, setCopyAlert] = useState(false);
  const [stockId, setStockId] = useState(false);

  const marketApiEndpoint = useSelector((state) =>
    get(state, ['qt', 'config', 'integrations', 'market-api', 'endpoint'], ''),
  );

  useEffect(() => {
    setStockId(fetchStockId());
  }, []);

  const { data, isLoading, isFetching, isError } = useQuery(
    ['basic-stock-details', stockId],
    () => fetchBasicDetailsAndSector(marketApiEndpoint),
    {
      enabled: !!marketApiEndpoint,
      refetchInterval: 30000, // 🔁 replaces setInterval
      refetchOnWindowFocus: false,
      staleTime: 15000,
    },
  );

  const basicStockData = data?.basicStockData || {};
  const stockSector = data?.stockSector || {};

  const result = stockParentData?.filter((stock) => stock?.STOCKID?.toString() === stockId);

  const copyToClipboardHandler = () => {
    navigator.clipboard.writeText(result[0]?.ISIN);
    setCopyAlert(true);
    setTimeout(() => setCopyAlert(false), 1000);
  };

  /* ---------------- States ---------------- */

  if (isLoading) {
    return <div styleName="initial-loading-container"></div>;
  }

  if (isError) {
    return (
      <div styleName="no-data-container-ext">
        <EmptyState />
      </div>
    );
  }

  return (
    <div styleName="basic-details-container">
      {isFetching && (
        <div styleName="loader-container">
          <Loader color="#dadee1" />
        </div>
      )}

      {stockParentData?.length === 0 && isEmpty(basicStockData) && isEmpty(stockSector) ? (
        <div styleName="no-data-container-ext">
          <div styleName="no-data-container-int">
            <EmptyState />
          </div>
        </div>
      ) : (
        <>
          {/* -------- Stock Header -------- */}
          <div styleName="stock-name-container">
            <div>
              {stockSector?.industry && <span styleName="sector">{stockSector.industry}</span>}
            </div>

            <span styleName="stock-name">{result[0]?.COMPNAME}</span>

            {result[0]?.ISIN && (
              <div styleName="isin-container">
                <span styleName="isin-header">ISIN</span>
                <span styleName="isin-value">{result[0]?.ISIN}</span>

                {!copyAlert ? (
                  <i
                    className="icon icon-ic_copy"
                    styleName="copy"
                    onClick={copyToClipboardHandler}
                  />
                ) : (
                  <span styleName="copy-alert">Copied ISIN Successfully!</span>
                )}
              </div>
            )}
          </div>

          {/* -------- NSE -------- */}
          {basicStockData?.nse && (
            <div styleName="stock-data-container">
              <div styleName="data-first-row">
                <span styleName="nse-tag">NSE</span>
                <span styleName="data-val">{formatNumber(basicStockData.nse.currentPrice)}</span>

                <div styleName="change-values">
                  <span styleName={basicStockData.nse.priceChange < 0 ? 'negative' : 'positive'}>
                    {formatNumber(basicStockData.nse.priceChange)}{' '}
                    {basicStockData.nse.priceChange < 0 ? '▼' : '▲'}
                    {formatNumber(basicStockData.nse.changePercentage)}%
                  </span>
                </div>
              </div>

              <div styleName="data-second-row">
                <div>
                  <p styleName="data-desc">Prev. Close</p>
                  <p styleName="data-desc-value">
                    {formatNumber(basicStockData.nse.previousClose)}
                  </p>
                </div>
                <div>
                  <p styleName="data-desc">Open</p>
                  <p styleName="data-desc-value">{formatNumber(basicStockData.nse.openPrice)}</p>
                </div>
                <div>
                  <p styleName="data-desc">High</p>
                  <p styleName="data-desc-value">{formatNumber(basicStockData.nse.dayHigh)}</p>
                </div>
                <div>
                  <p styleName="data-desc">Low</p>
                  <p styleName="data-desc-value">{formatNumber(basicStockData.nse.dayLow)}</p>
                </div>
              </div>
            </div>
          )}

          {/* -------- BSE -------- */}
          {basicStockData?.bse && (
            <div styleName="stock-data-container">
              <div styleName="data-first-row">
                <span styleName="bse-tag">BSE</span>
                <span styleName="data-val">{formatNumber(basicStockData.bse.currentPrice)}</span>

                <div styleName="change-values">
                  <span styleName={basicStockData.bse.priceChange < 0 ? 'negative' : 'positive'}>
                    {formatNumber(basicStockData.bse.priceChange)}{' '}
                    {basicStockData.bse.priceChange < 0 ? '▼' : '▲'}
                    {formatNumber(basicStockData.bse.changePercentage)}%
                  </span>
                </div>
              </div>

              <div styleName="data-second-row">
                <div>
                  <p styleName="data-desc">Prev. Close</p>
                  <p styleName="data-desc-value">
                    {formatNumber(basicStockData.bse.previousClose)}
                  </p>
                </div>
                <div>
                  <p styleName="data-desc">Open</p>
                  <p styleName="data-desc-value">{formatNumber(basicStockData.bse.openPrice)}</p>
                </div>
                <div>
                  <p styleName="data-desc">High</p>
                  <p styleName="data-desc-value">{formatNumber(basicStockData.bse.dayHigh)}</p>
                </div>
                <div>
                  <p styleName="data-desc">Low</p>
                  <p styleName="data-desc-value">{formatNumber(basicStockData.bse.dayLow)}</p>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

BasicDetailsStock.propTypes = {
  stockParentData: PropTypes.array.isRequired,
};

export default BasicDetailsStock;
